/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// State
import { TSelectOption } from '@global-types/index';
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';
import {
	TParsedSite,
	TSite,
	TSiteQueryParams,
} from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface ISiteSlice {
	/* Pagination */
	sitesTotalPages: number;
	sitesPage: number;
	sitesTotal: number;
	sitesPageSize: number;

	/* Datatable */
	sitesSelectedRecords: TParsedSite[];
	sitesDefaultSelectedFields: TSelectOption[];
	sitesOpenConfirmationModalDelete: {
		open: boolean;
		params: {
			id?: string;
		};
	};
	sitesQueryParams: TSiteQueryParams;
	sitesSortStatus: Partial<TSite>;
	sitesSortParams: string;
	sitesSelectedRow: Partial<TParsedSite>;
	sitesLastAccessTokenCreated: string | null;
	apiTokenOrganization: string | null;
	openDrawerSiteForm: boolean;
	paramsFromProject: {
		project?: string;
		site?: string;
		producer?: string;
	};
	setParamsFromProject: (_paramsFromProject: {
		project?: string;
		site?: string;
		producer?: string;
	}) => void;

	setSitesSelectedRow: (
		_sitesSelectedRow: Partial<TParsedSite>
	) => void;

	setSiteOrganization: (
		_organizationId: string | null
	) => void;
	setSitesPage: (_sitesPage: number) => void;
	setSitesSelectedRecords: (
		_sitesSelectedRecords: TParsedSite[]
	) => void;
	setSitesOpenConfirmationModalDelete: (_sitesOpenConfirmationModalDelete: {
		open: boolean;
		params: {
			id?: string;
		};
	}) => void;
	setSitesQueryParams: (
		_sitesQueryParams: TSiteQueryParams
	) => void;
	setSitesSortStatus: (
		_sitesSortStatus: Partial<TSite>
	) => void;
	setSitesSortParams: (_sitesSortParams: string) => void;

	setSitesPageSize: (_pageSize: number) => void;
	setSitesDefaultSelectedFields: (
		_sitesDefaultSelectedFields: TSelectOption[]
	) => void;
	setOpenDrawerSiteForm: (
		_openDrawerSiteForm: boolean
	) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	/* Pagination */
	sitesTotal: 0,
	sitesPage: 1,
	sitesPageSize: 100,
	sitesTotalPages: 0,
	/**************/
	openDrawerSiteForm: false,
	sites: [],
	sitesSelectedRecords: [],
	sitesDefaultSelectedFields: [],
	sitesQueryParams: {},
	sitesSelectedRow: {},
	sitesSortParams: '-createdAt',
	apiTokenOrganization: null,
	sitesSortStatus: {},
	sitesLastAccessTokenCreated: null,
	paramsFromProject: {},
	sitesOpenConfirmationModalDelete: {
		id: '',
		open: false,
		params: '',
	},
	sitesOpenConfirmationModalVerify: {
		open: false,
		params: '',
	},
	sitesOpenConfirmationModalMint: {
		open: false,
		params: '',
	},
};

export const createSitesSlice: StateCreator<
	TStore,
	[],
	[],
	ISiteSlice
> = (set) => ({
	...initialState,
	setOpenDrawerSiteForm: (_openDrawerSiteForm: boolean) =>
		set({
			openDrawerSiteForm: _openDrawerSiteForm,
		}),
	setSitesDefaultSelectedFields: (
		_sitesDefaultSelectedFields: TSelectOption[]
	) =>
		set({
			sitesDefaultSelectedFields:
				_sitesDefaultSelectedFields,
		}),
	setSitesSelectedRow: (
		_sitesSelectedRow: Partial<TParsedSite>
	) =>
		set({
			sitesSelectedRow: _sitesSelectedRow,
		}),
	setSiteOrganization: (_organizationId: string | null) =>
		set({ apiTokenOrganization: _organizationId }),
	setSitesLastAccessTokenCreated: (
		_sitesLastAccessTokenCreated: string
	) =>
		set({
			sitesLastAccessTokenCreated:
				_sitesLastAccessTokenCreated,
		}),
	setParamsFromProject: (_paramsFromProject) =>
		set({ paramsFromProject: _paramsFromProject }),
	setSitesSortParams: (_sortStatus: string) =>
		set({ sitesSortParams: _sortStatus }),
	setSitesSortStatus: (_sortStatus: Partial<TSite>) =>
		set({ sitesSortStatus: _sortStatus }),
	setSitesQueryParams: (_queryParams: TSiteQueryParams) =>
		set({ sitesQueryParams: _queryParams }),
	setSitesTotal: (_total: number) =>
		set({ sitesTotal: _total }),
	setSitesOpenConfirmationModalDelete:
		(_sitesOpenConfirmationModalDelete: {
			open: boolean;
			params: {
				id?: string;
			};
		}) =>
			set({
				sitesOpenConfirmationModalDelete:
					_sitesOpenConfirmationModalDelete,
			}),
	setSitesPage: (_page: number) =>
		set({ sitesPage: _page }),
	setSitesPageSize: (_pageSize: number) =>
		set({ sitesPageSize: _pageSize }),
	setSitesTotalPages: (_totalPages: number) =>
		set({ sitesTotalPages: _totalPages }),
	setSitesSelectedRecords: (
		_sitesSelectedRecords: TParsedSite[]
	) =>
		set({
			sitesSelectedRecords: _sitesSelectedRecords,
		}),
});
